import { useEffect } from 'react';

interface MetaInput {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_URL =
  (import.meta.env.VITE_SITE_URL as string | undefined) ??
  'https://yourlanecoaching.com';
const SITE_NAME = 'Your Lane Coaching';
// Derived from Megan's supplied banner artwork. public/og-default.png is kept
// on disk as a one-line revert.
const DEFAULT_OG = '/og-yl-banner.jpg';

function upsertMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id: string, data: Record<string, unknown> | Record<string, unknown>[]) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * Sets <title>, description, OG, Twitter, canonical, and (optional) JSON-LD
 * for the current page. Effects run client-side; index.html supplies the
 * SSR-safe defaults for crawlers that don't execute JS.
 */
export function useDocumentMeta(input: MetaInput) {
  useEffect(() => {
    const url = `${SITE_URL.replace(/\/$/, '')}${input.path}`;
    const og = input.ogImage ?? DEFAULT_OG;

    document.title = input.title;
    upsertMeta('name', 'description', input.description);
    upsertLink('canonical', url);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', input.title);
    upsertMeta('property', 'og:description', input.description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', og.startsWith('http') ? og : `${SITE_URL}${og}`);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', input.title);
    upsertMeta('name', 'twitter:description', input.description);
    upsertMeta('name', 'twitter:image', og.startsWith('http') ? og : `${SITE_URL}${og}`);

    if (input.schema) {
      upsertJsonLd('jsonld-page', input.schema as Record<string, unknown>);
    } else {
      document.getElementById('jsonld-page')?.remove();
    }
  }, [input.title, input.description, input.path, input.ogImage, JSON.stringify(input.schema ?? null)]);
}
